var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { findDailyLessonIconElement, findFirstDailyLessonIconElement } from '../molecules/home_daily_lesson_icon';
import { HomeDailyLessonPopover } from '../molecules/home_daily_lesson_popover';
import { HomeDailyLessonDiagnosisPopover } from '../molecules/home_daily_lesson_diagnosis_popover';
import { HomeDailyLessonRediagnosisPopover } from '../molecules/home_daily_lesson_rediagnosis_popover';
import { HomeDailyLessonFuturePopover } from '../molecules/home_daily_lesson_future_popover';
import { HOME_MAP_SCROLL_ELEMENT_ID } from '../pages/main_home';
import { useXlMediaQuery } from '@lean-body/src/util';
const buildDailyLessonPopoverTitle = (day) => {
    var _a;
    const lesson = (_a = day.dailyLessonDayLesson) === null || _a === void 0 ? void 0 : _a.lesson;
    if (!lesson) {
        return '';
    }
    return `${lesson.duration}min ${lesson.name}`;
};
// HOMEマップのレッスン吹き出しの位置計測・開閉制御・描画を担うレイヤー
export const HomeDailyLessonPopoverLayer = ({ mapShellRef, popoverLayerRef, selectedKey, renderedKey, renderedItem, setSelectedKey, setRenderedKey, diagnosisDayId, isRediagnosisPopover, onStartDay, onChangeDay, onDiagnosisStart, }) => {
    var _a;
    const popoverLayer = popoverLayerRef.current;
    const isXl = useXlMediaQuery();
    const [popoverPosition, setPopoverPosition] = useState(null);
    const isInitialScrollRef = useRef(renderedKey != null);
    const isPopoverClosing = selectedKey == null && renderedKey != null;
    const renderedDay = (_a = renderedItem === null || renderedItem === void 0 ? void 0 : renderedItem.day) !== null && _a !== void 0 ? _a : null;
    const onClickStartDay = (day) => {
        setSelectedKey(null);
        onStartDay(day);
    };
    const onClickChangeDay = (day) => __awaiter(void 0, void 0, void 0, function* () {
        yield onChangeDay(day);
    });
    // 吹き出しの外側（マップの別の場所など）をクリックしたら吹き出しを閉じる
    useEffect(() => {
        if (selectedKey == null) {
            return;
        }
        const onClickOutside = () => setSelectedKey(null);
        document.addEventListener('click', onClickOutside);
        return () => document.removeEventListener('click', onClickOutside);
    }, [selectedKey]);
    // 対象アイコン（renderedKey）の位置から吹き出し位置(top/left/arrowLeft)を計測してセットする
    const applyPopoverPosition = useCallback(() => {
        const shell = mapShellRef.current;
        if (renderedKey == null || shell == null) {
            setPopoverPosition(null);
            return;
        }
        const iconEl = findDailyLessonIconElement(shell, renderedKey);
        if (iconEl == null) {
            setPopoverPosition(null);
            return;
        }
        const POPOVER_HALF_WIDTH = 170; // 吹き出し幅 340 の半分
        const ARROW_HALF_WIDTH = 34; // 矢印幅 68 の半分
        const iconRect = iconEl.getBoundingClientRect();
        const shellRect = shell.getBoundingClientRect();
        const topVp = iconRect.bottom + 6; // アイコン下端 + 余白（ビューポート基準）
        const leftVp = shellRect.left + shellRect.width / 2 - POPOVER_HALF_WIDTH; // マップ中央に吹き出しを寄せる（ビューポート基準）
        const arrowLeft = iconRect.left + iconRect.width / 2 - leftVp - ARROW_HALF_WIDTH; // 矢印をアイコンの中心に合わせる
        const top = isXl ? topVp : topVp + window.scrollY;
        const left = isXl ? leftVp : leftVp + window.scrollX;
        setPopoverPosition({ top, left, arrowLeft });
    }, [renderedKey, mapShellRef, isXl]);
    const positionPopover = useCallback((withScroll) => {
        var _a;
        const shell = mapShellRef.current;
        if (renderedKey == null || shell == null) {
            setPopoverPosition(null);
            return;
        }
        const iconEl = findDailyLessonIconElement(shell, renderedKey);
        if (iconEl == null) {
            setPopoverPosition(null);
            return;
        }
        applyPopoverPosition();
        if (!withScroll) {
            return;
        }
        // スクロール対象は XL=内部要素(#homeMapScroll) / XL未満=window
        const internalScrollEl = isXl ? document.getElementById(HOME_MAP_SCROLL_ELEMENT_ID) : null;
        const canScroll = isXl ? internalScrollEl != null : true;
        const firstIconEl = findFirstDailyLessonIconElement(shell);
        if (!canScroll || firstIconEl == null) {
            return;
        }
        const currentScrollTop = isXl ? ((_a = internalScrollEl === null || internalScrollEl === void 0 ? void 0 : internalScrollEl.scrollTop) !== null && _a !== void 0 ? _a : 0) : window.scrollY;
        const maxScrollTop = isXl
            ? internalScrollEl
                ? internalScrollEl.scrollHeight - internalScrollEl.clientHeight
                : 0
            : document.documentElement.scrollHeight - window.innerHeight;
        const desiredScrollTop = Math.max(0, Math.min(maxScrollTop, iconEl.getBoundingClientRect().top - firstIconEl.getBoundingClientRect().top));
        if (Math.abs(desiredScrollTop - currentScrollTop) < 1) {
            return;
        }
        const behavior = isInitialScrollRef.current ? 'auto' : 'smooth';
        isInitialScrollRef.current = false;
        if (isXl) {
            internalScrollEl === null || internalScrollEl === void 0 ? void 0 : internalScrollEl.scrollTo({ top: desiredScrollTop, behavior });
        }
        else {
            window.scrollTo({ top: desiredScrollTop, behavior });
        }
    }, [renderedKey, mapShellRef, isXl, applyPopoverPosition]);
    // 吹き出しを開いたとき：マウント直後は Aphrodite のスタイル適用前で要素サイズが未確定なため、スタイル適用後に計測・スクロールする
    useLayoutEffect(() => {
        if (renderedKey == null) {
            setPopoverPosition(null);
            return;
        }
        const rafId = requestAnimationFrame(() => positionPopover(true));
        return () => cancelAnimationFrame(rafId);
    }, [renderedKey, positionPopover]);
    // スクロール中（コマクリック時のプログラム的スクロール・ツールバー表示切替など）は実アイコン位置に追従して再配置する
    useEffect(() => {
        if (selectedKey == null || !isXl) {
            return;
        }
        const scrollTarget = document.getElementById(HOME_MAP_SCROLL_ELEMENT_ID);
        if (scrollTarget == null) {
            return;
        }
        const onScroll = () => applyPopoverPosition();
        scrollTarget.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollTarget.removeEventListener('scroll', onScroll);
    }, [selectedKey, isXl, applyPopoverPosition]);
    // 画面サイズ変更時：アイコン位置が変わるため、スクロールはせず吹き出し位置だけ再計算する
    useEffect(() => {
        if (renderedKey == null) {
            return;
        }
        const onResize = () => positionPopover(false);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [renderedKey, positionPopover]);
    // ユーザー操作によるスクロールでのみ吹き出しを閉じる
    // 初期表示・アイコンクリック時のスクロールでは発火しないよう、scroll ではなく wheel/touchmove を監視する
    useEffect(() => {
        if (selectedKey == null) {
            return;
        }
        // スクロール対象は XL=内部要素 / XL未満=window（文書スクロール）
        const scrollTarget = isXl
            ? document.getElementById(HOME_MAP_SCROLL_ELEMENT_ID)
            : window;
        if (scrollTarget == null) {
            return;
        }
        const onUserScroll = () => {
            setSelectedKey(null);
            setRenderedKey(null);
        };
        scrollTarget.addEventListener('wheel', onUserScroll, { passive: true });
        scrollTarget.addEventListener('touchmove', onUserScroll, { passive: true });
        return () => {
            scrollTarget.removeEventListener('wheel', onUserScroll);
            scrollTarget.removeEventListener('touchmove', onUserScroll);
        };
    }, [selectedKey, isXl]);
    if (renderedItem == null || popoverPosition == null || popoverLayer == null) {
        return null;
    }
    // 再診断対象は再診断バリアント、それ以外（未診断プレビュー）は初回診断バリアントを出す
    const DiagnosisPopover = isRediagnosisPopover ? HomeDailyLessonRediagnosisPopover : HomeDailyLessonDiagnosisPopover;
    return createPortal(renderedDay != null && renderedDay.id === diagnosisDayId ? (React.createElement(DiagnosisPopover, { key: renderedItem.key, top: popoverPosition.top, left: popoverPosition.left, arrowLeft: popoverPosition.arrowLeft, isClosing: isPopoverClosing, positionAbsolute: !isXl, onExited: () => setRenderedKey(null), onClick: () => onDiagnosisStart() })) : renderedItem.state === 'future' || renderedDay == null ? (React.createElement(HomeDailyLessonFuturePopover, { key: renderedItem.key, top: popoverPosition.top, left: popoverPosition.left, arrowLeft: popoverPosition.arrowLeft, isClosing: isPopoverClosing, positionAbsolute: !isXl, onExited: () => setRenderedKey(null) })) : (React.createElement(HomeDailyLessonPopover, { key: renderedItem.key, title: buildDailyLessonPopoverTitle(renderedDay), thumbnailUrl: renderedDay.dailyLessonDayLesson.lesson.thumbnailURL, duration: renderedDay.dailyLessonDayLesson.lesson.duration, showChangeButton: renderedDay.isCurrent, top: popoverPosition.top, left: popoverPosition.left, arrowLeft: popoverPosition.arrowLeft, isClosing: isPopoverClosing, positionAbsolute: !isXl, onExited: () => setRenderedKey(null), onClickStart: () => onClickStartDay(renderedDay), onClickChange: () => onClickChangeDay(renderedDay) })), popoverLayer);
};
